import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, MessageCircle, Sparkles, ShieldAlert } from 'lucide-react';
import { paymentAgentsAPI, fmtError } from '@/lib/api';

/**
 * Deriv-style payment agents directory. Pure marketing + discovery surface.
 *
 * - Agents only see this when they're "active" (admin-approved).
 * - Off-platform settlement: trader contacts the agent directly. Admin
 *   manually marks the order paid afterwards in /admin/orders.
 * - Designed to convert: a fat "Become a Payment Agent" CTA + commission tag.
 */
export default function PaymentAgents() {
  const [agents, setAgents] = useState([]);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');

  useEffect(() => {
    paymentAgentsAPI.list().then(setAgents).catch(e => setErr(fmtError(e)));
  }, []);

  const countries = useMemo(() => {
    const set = new Set(agents.map(a => a.country).filter(Boolean));
    return Array.from(set).sort();
  }, [agents]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return agents.filter(a => {
      if (country && a.country !== country) return false;
      if (!needle) return true;
      return (
        (a.name || '').toLowerCase().includes(needle)
        || (a.description || '').toLowerCase().includes(needle)
        || (a.methods || []).some(m => m.toLowerCase().includes(needle))
      );
    });
  }, [agents, q, country]);

  return (
    <div className="space-y-8" data-testid="payment-agents-page">
      <div className="card-tech rounded-lg p-6 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[360px] h-[360px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(0,255,102,0.18), transparent)' }} />
        <div className="relative">
          <div className="label-sm">Payment Agents</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Pay your way. Bank transfer, cash, e-wallet — agents you can talk to.</h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
            Prefer paying through a local payment agent instead of crypto? Browse approved agents below, message them directly, and complete payment via the method they offer.
            Once they confirm your payment, we'll activate your evaluation account.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/payment-agents/apply" className="btn-primary btn-sm" data-testid="become-agent-btn">
              <Sparkles size={14} /> Become a Payment Agent
            </Link>
            <a href="#agents" className="btn-ghost btn-sm">Browse agents</a>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3" id="agents">
        <div className="flex-1 relative">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-zinc-500 pointer-events-none" />
          <input
            className="input-tech pl-9"
            placeholder="Search by name, method, or location"
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="agents-search"
          />
        </div>
        {countries.length > 0 && (
          <select className="input-tech sm:w-56" value={country} onChange={e => setCountry(e.target.value)} data-testid="agents-country">
            <option value="">All countries</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {err && <div className="text-sm text-[#ff3366]">{err}</div>}

      {filtered.length === 0 ? (
        <div className="card-tech rounded-lg p-10 text-center">
          <div className="font-display text-xl">No agents available yet</div>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
            We're onboarding agents now. In the meantime, check out
            our <Link to="/plans" className="text-[#00ff66] hover:underline">crypto checkout</Link> — it's instant.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(a => <AgentCard key={a.agent_id} a={a} />)}
        </div>
      )}

      <div className="card-tech rounded-lg p-5 flex gap-3 items-start" data-testid="agents-disclaimer">
        <ShieldAlert size={16} className="text-[#ff8c42] mt-0.5 shrink-0" />
        <div className="text-xs text-zinc-400 leading-relaxed">
          <span className="text-white">Traders Spring Group is not affiliated with any payment agents.</span> Payment agents are independent merchants. They are not employees, contractors or partners of Traders Spring Group. Always verify the agent's identity and use due caution. We are not liable for any transactions you make with them.
        </div>
      </div>
    </div>
  );
}

function AgentCard({ a }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="card-tech rounded-lg p-5 flex flex-col" data-testid={`agent-card-${a.agent_id}`}
    >
      <div className="flex items-center gap-3">
        {a.logo_url
          ? <img src={a.logo_url} alt="" className="w-12 h-12 rounded-full object-cover bg-black/40" onError={e => e.target.style.display = 'none'} />
          : <div className="w-12 h-12 rounded-full bg-[#00ff66]/10 border border-[#00ff66]/40 flex items-center justify-center font-display text-lg text-[#00ff66]">{a.name?.[0]?.toUpperCase() || 'A'}</div>}
        <div className="min-w-0">
          <div className="font-display text-lg truncate">{a.name}</div>
          {a.country && (
            <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
              <MapPin size={11} /> {a.country}
            </div>
          )}
        </div>
      </div>
      {a.description && <p className="text-sm text-zinc-400 mt-3 line-clamp-3">{a.description}</p>}
      {(a.methods || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {a.methods.map(m => (
            <span key={m} className="text-[10px] tracking-widest font-mono text-zinc-300 px-2 py-1 rounded-full bg-white/5 border border-white/10">{m}</span>
          ))}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-white/5 flex justify-end">
        <a
          href={a.contact_url}
          target="_blank"
          rel="noreferrer"
          className="btn-primary btn-sm"
          data-testid={`agent-contact-${a.agent_id}`}
        >
          <MessageCircle size={14} /> Contact
        </a>
      </div>
    </motion.div>
  );
}
